'use client'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { medusaClient, queryClient } from '@lib/config'
import { useAccount } from '@lib/context/account-context'
import Button from '@modules/common/components/button'
import Input from '@modules/common/components/input'
import Link from 'next/link'
import { FieldValues, useForm } from 'react-hook-form'
import { handleAuthError } from '@lib/util/handle-error'
import {
  Box,
  Button as ButtonChakra,
  Checkbox,
  Flex,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useDisclosure
} from '@chakra-ui/react'
import PhoneNumberInput from '@components/phone-number-input'
import {
  MIN_LENGTH_PASSWORD,
  MIN_LENGTH_PASSWORD_MESSAGE,
  OTP_DISABLED
} from '@constants/auth.constant'
import { AVOCADO_DISABLED } from '@lib/constants'
import { sendOtp } from '@lib/util/customer-handle'
import Otp from './otp'
import AuthLayout from '@layouts/auth-layout'
import AuthForm from '../auth-form'
import Image from 'next/image'
import qs from 'qs'

import BgRegister from '@images/auth/bg-register.png'
import RouterUrl from '@constants/router.constant'
import { LOCAL_PHONE_VERIFY } from '@constants/local-storage'
import { CheckIcon } from '@chakra-ui/icons'
import axios from 'axios'
import { AVOCADO_CONSTANT } from '@lib/constants'
import { useAlert, useResponsive } from '@common/hooks'
import useAdvocado from '@common/hooks/use-advocado'
import TermConditionModal from '@components/modals/term-condtion-modal'

export interface RegisterCredentials extends FieldValues {
  first_name: string
  last_name: string
  email: string
  password: string
  phone?: string
  date_of_birth?: string
  gender?: string
  confirm?: string
  pin?: string
}

export const COUNTDOWN = 10 * 60 * 1000

enum Gender {
  FEMALE = 'female',
  MALE = 'male'
}

const Register = () => {
  const { getAdvocadoToken, getAdvocadoUser } = useAdvocado()
  const { setAvocado, refetchCustomer, avocado } = useAccount()
  const [authError, setAuthError] = useState<string | undefined>(undefined)
  const [phoneError, setPhoneError] = useState<string | undefined>(undefined)
  const [pinError, setPinError] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [otpVerified, setOtpVerified] = useState(false)
  const [agree, setIsAgree] = useState(false)
  const verifyBtn = useRef<HTMLButtonElement>(null)

  const [success, setSuccess] = useState(false)
  const [sentOtp, setSentOtp] = useState(false)

  const [isUserAdvocado, setIsUserAdvocado] = useState(false)

  const { successToast } = useAlert()
  const router = useRouter()

  const { mdMax } = useResponsive()

  const { isOpen, onOpen, onClose } = useDisclosure()

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    setValue
  } = useForm<RegisterCredentials>()

  const onSendOtp = async () => {
    setPhoneError('')
    setSending(true)

    const phone = getValues('phone')
    try {
      const token = await getAdvocadoToken()

      const user = await getAdvocadoUser(token as string, phone as string)

      setIsUserAdvocado(!!user)

      sendOtp({ phone: phone as string })
        .then(() => {
          successToast({
            title: 'Resend Successfully',
            description: `A new OTP has been sent to your whatsapp`
          })
          setSending(false)
          localStorage.setItem(LOCAL_PHONE_VERIFY, phone as string)
          setSentOtp(true)
          setCountdown(COUNTDOWN)
        })
        .catch((e) => {
          handleAuthError(e, (mess) => {
            setPhoneError(mess)
          })
          setSending(false)
        })
    } catch (e) {
      handleAuthError(e, (mess) => {
        setPhoneError(mess)
      })
      setSending(false)
    }
  }

  const onSubmit = handleSubmit(async (credentials) => {
    // TEMP (OTP disabled): skip the OTP verification requirement when OTP_DISABLED
    if (!OTP_DISABLED && otpVerified === false) {
      // focus on verify button
      verifyBtn.current?.focus()
      return
    }

    setAuthError('')
    setPinError('')
    setPhoneError('')
    setLoading(true)

    if (credentials.password !== credentials.confirm) {
      setAuthError('Confirm password not match with password')
      setLoading(false)
      return
    }

    if (agree && isUserAdvocado === false) {
      if (
        (credentials.pin as string).length !== 4 ||
        (credentials.pin as string).match(/[0-9]{4}/g) === null
      ) {
        setPinError('Pin invalid')
        setLoading(false)
        return
      }
    }

    const payload = {
      first_name: credentials.first_name,
      last_name: credentials.last_name,
      email: credentials.email,
      password: credentials.password,
      phone: credentials.phone,
      date_of_birth:
        credentials.date_of_birth != null
          ? credentials.date_of_birth.length > 0
            ? credentials.date_of_birth
            : undefined
          : undefined,
      gender: credentials.gender,
      is_verify_otp: otpVerified
    }

    medusaClient.customers
      .create(payload as any)
      .then(async () => {
        // Start the new account from a clean cache (both success branches below).
        queryClient.clear()
        refetchCustomer()
        localStorage.removeItem(LOCAL_PHONE_VERIFY)
        // TEMP (OTP disabled): with OTP_DISABLED there is no Advocado flow (it needs phone/OTP),
        // so the `agree` branch would hang on an infinite loading state (waiting for an
        // avocado.token that never arrives). Always finish by redirecting home.
        // Remove `|| OTP_DISABLED` to restore.
        if (!agree || OTP_DISABLED) {
          setLoading(false)
          router.replace(RouterUrl.HOME.ROOT)
        } else {
          setSuccess(true)
        }
      })
      .catch((e: any) => {
        handleAuthError(e, (mess) => {
          setAuthError(mess)
        })
        setLoading(false)
      })
  })

  useEffect(() => {
    // TEMP (Avocado disabled): never create an Advocado customer after register.
    // (avocado.token also never loads while disabled; this is a safety net.)
    if (AVOCADO_DISABLED) {
      return
    }
    if (avocado?.token != null && success === true) {
      const pin = getValues('pin')
      const phone = getValues('phone')

      axios
        .post(
          AVOCADO_CONSTANT.create_customer,
          qs.stringify({
            countryCallingCode: avocado?.phone_code ?? '65',
            phoneNumber: avocado?.phone_num ?? phone,
            pin
          }),
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              Authorization: 'Bearer ' + avocado?.token
            }
          }
        )
        .then(({ data }) => {
          router.replace(RouterUrl.HOME.ROOT)
          axios
            .post(
              AVOCADO_CONSTANT.accept_pdpa,
              qs.stringify({
                countryCallingCode: avocado?.phone_code ?? '65',
                phoneNumber: avocado?.phone_num ?? phone,
                pdpa: '1'
              }),
              {
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                  Authorization: 'Bearer ' + avocado?.token
                }
              }
            )
            .then(() => {
              router.replace(RouterUrl.HOME.ROOT)
            })
            .catch((e) => {
              router.replace(RouterUrl.HOME.ROOT)
            })
        })
        .catch((e) => {
          router.replace(RouterUrl.HOME.ROOT)
        })
    }
  }, [avocado, success])

  return (
    <AuthLayout
      title="Create Habbit Account"
      description={
        <Text mx={'auto'} my={16}>
          {/* TEMP (reward-points hidden): removed the link to /reward-points. Re-wrap with <Link href={RouterUrl.REWARD_POINTS.ROOT}> to restore. */}
          {/* <Image
            src={BgRegister}
            alt="bg-register"
            width={500}
            height={300}
            style={{
              width: mdMax ? '100%' : '70%',
              margin: 'auto'
            }}
          /> */}
        </Text>
      }
    >
      <TermConditionModal isOpen={isOpen} onClose={onClose} />

      <AuthForm
        className="mx-auto sm:w-8/12 md:w-6/12 lg:w-6/12"
        onSubmit={onSubmit}
        flexProps={{ gap: 7 }}
      >
        <Input
          label="First name"
          {...register('first_name', {
            required: 'First name is required'
          })}
          autoComplete="given-name"
          errors={errors}
          disabled={loading}
          required={true}
        />
        <Input
          label="Last name"
          {...register('last_name', {
            required: 'Last name is required'
          })}
          autoComplete="family-name"
          errors={errors}
          disabled={loading}
          required={true}
        />
        <Input
          label="Email"
          {...register('email', { required: 'Email is required' })}
          autoComplete="email"
          errors={errors}
          disabled={loading}
          required={true}
        />
        {/* ====================== Start ============================= */}
        {/* TEMP (OTP disabled): hide the phone field entirely when OTP_DISABLED (can't verify, so don't collect phone) */}
        {!OTP_DISABLED && (!sentOtp || otpVerified) && (
          <Flex gap={2}>
            <Box flex={1}>
              <PhoneNumberInput
                control={control as any}
                {...register('phone', {
                  required: 'Phone is required',
                  pattern: {
                    value:
                      /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4,})$/,
                    message: 'Phone number invalid'
                  }
                })}
                disabled={otpVerified}
              />
            </Box>
            {/* TEMP (OTP disabled): hide the Verify button when OTP_DISABLED */}
            {!OTP_DISABLED && (
              <ButtonChakra
                bg={'bg'}
                borderRadius={0}
                _hover={{
                  bg: 'bg'
                }}
                isLoading={sending}
                type="button"
                onClick={onSendOtp}
                isDisabled={otpVerified}
                rightIcon={<>{otpVerified && <CheckIcon />}</>}
                py={'25px'}
                ref={verifyBtn}
                _focus={{
                  outline: '2px solid red'
                }}
              >
                <Text
                  fontSize={'18px'}
                  fontFamily={'DM Sans Regular, sans-serif'}
                >
                  {otpVerified === true ? 'Verified' : 'Verify'}
                </Text>
              </ButtonChakra>
            )}
          </Flex>
        )}
        {phoneError && (
          <div>
            <span className="w-full text-rose-500 text-medium-regular">
              {phoneError}
            </span>
          </div>
        )}
        {sentOtp && !otpVerified && (
          <Otp
            phone={getValues('phone') as string}
            countdownOtp={countdown}
            onSetOtpVerified={(input: boolean) => {
              setOtpVerified(input)
            }}
          />
        )}

        {/* ====================== End ============================= */}
        <Flex
          alignItems={{ base: 'start', md: 'center' }}
          gap={5}
          flexDirection={{ base: 'column', md: 'row' }}
        >
          <Flex flex={1.5} w={'100%'}>
            <Input
              label="Date of Birth"
              {...register('date_of_birth')}
              type="date"
              errors={errors}
              disabled={loading}
            />
          </Flex>
          <Flex flex={1} gap={4}>
            <Text fontSize={'20px'}>Gender:</Text>
            <RadioGroup
              onChange={(e: any) => {
                setValue('gender', e)
              }}
            >
              <Stack direction="row">
                <Radio value={Gender.FEMALE} size={'lg'}>
                  <Text fontSize={'20px'}>Female</Text>
                </Radio>
                <Radio value={Gender.MALE} size={'lg'}>
                  <Text fontSize={'20px'}>Male</Text>
                </Radio>
              </Stack>
            </RadioGroup>
          </Flex>
        </Flex>
        <Input
          label="Password"
          {...register('password', {
            required: 'Password is required',
            minLength: {
              value: MIN_LENGTH_PASSWORD,
              message: MIN_LENGTH_PASSWORD_MESSAGE
            }
          })}
          type="password"
          autoComplete="new-password"
          errors={errors}
          disabled={loading}
          required={true}
        />
        <Input
          label="Confirm Password"
          {...register('confirm', {
            required: 'Password is required',
            minLength: {
              value: MIN_LENGTH_PASSWORD,
              message: MIN_LENGTH_PASSWORD_MESSAGE
            }
          })}
          type="password"
          autoComplete="new-password"
          errors={errors}
          disabled={loading}
          required={true}
        />
        {authError && (
          <div>
            <span className="w-full text-rose-500 text-medium-regular">
              {authError}
            </span>
          </div>
        )}

        {/* TEMP (Avocado disabled): hide the Habbit Points Membership opt-in.
            `agree` stays false so no Advocado signup/PIN logic can run. */}
        {!AVOCADO_DISABLED && (
          <Flex gap={2}>
            <Checkbox
              onChange={(e) => {
                setIsAgree(e.target.checked)
                setValue('pin', undefined)
              }}
              alignItems={'start'}
              mt={1}
              size={'md'}
            ></Checkbox>
            <Box fontSize={'18px'} fontFamily={'DM Sans Regular, sans-serif'}>
              I agree to apply for Habbit Points Membership on the{' '}
              <Text
                color={'red'}
                textDecoration={'underline'}
                display={'inline'}
                onClick={() => {
                  onOpen()
                }}
                cursor={'pointer'}
              >
                terms and conditions
              </Text>{' '}
              governing Habbit and give consent to providing my personal and
              contact information to JML Singapore Pte Ltd through SMS, email,
              direct mailer and phone. I confirm that all information submitted
              by me is true and correct. You may withdraw your consent to the
              collection, use and disclosure of your Personal Data with respect
              to the Habbit Points Membership Program by providing thirty (30)
              days written notice to: dpo@advocado.me.
              <Text color={'red'} fontWeight={600}>
                *Uncheck the box if you are not keen to collect Habbit Points,
                but would like to register an account with us.
              </Text>
            </Box>
          </Flex>
        )}
        {!AVOCADO_DISABLED &&
          agree === true &&
          isUserAdvocado === false &&
          (sentOtp || OTP_DISABLED) && (
            <>
              <Input
                label="4 Digit Pin for Points Redemption"
                {...register('pin', {
                  required: 'Pin is required'
                })}
                type="password"
                errors={errors}
                disabled={loading}
              />
              {pinError && (
                <div>
                  <span className="w-full text-rose-500 text-medium-regular">
                    {pinError}
                  </span>
                </div>
              )}
            </>
          )}
        <Box fontSize={'18px'} fontFamily={'DM Sans Regular, sans-serif'}>
          By creating an account, you agree to Habbit's{' '}
          <Link
            style={{ textDecoration: 'underline' }}
            href={RouterUrl.CUSTOMER_SERVICE.TERMS_CONDITIONS}
          >
            Terms & Conditions.
          </Link>
        </Box>
        <Button
          className="mt-6"
          isLoading={loading}
          // disabled={loading || !otpVerified}
          disabled={loading}
          type="submit"
        >
          Register
        </Button>
        <Text fontSize={'18px'} my={6} textAlign={'center'}>
          Already a member?{' '}
          <button
            onClick={() => {
              router.replace(RouterUrl.AUTH.LOGIN)
            }}
            className="underline"
          >
            Login
          </button>
          .
        </Text>
      </AuthForm>
    </AuthLayout>
  )
}

export default Register
